/**
 * 프롬프트 토큰 집계 — 결정 #12. 조립된 요청이 컨텍스트 한도 대비 얼마나
 * 쓰는지 추정한다(UI의 컨텍스트 사용량 표시용). build-prompt의 트림 계산과
 * 동일한 per-turn 오버헤드(+4)를 써서 일관성을 유지한다.
 *
 * 플랫폼 중립(결정 #10): 토큰 카운터 주입식이라 정확 토크나이저(tiktoken WASM,
 * lazy-load)로 교체 가능. 기본은 휴리스틱.
 */
import type { ChatTurn, CompletionRequest } from "../ports/ai.js";
import { heuristicTokenCount, type TokenCounter } from "./tokens.js";
import { estimateTokenCost, resolvePricing } from "../domain/model-metadata.js";

/** 역할/구분 오버헤드 근사(build-prompt와 동일). */
const PER_TURN_OVERHEAD = 4;

/** 채팅 턴 배열의 총 토큰을 추정한다(per-turn 오버헤드 포함). */
export function estimateTurnsTokens(
  turns: ChatTurn[],
  count: TokenCounter = heuristicTokenCount,
): number {
  let total = 0;
  for (const t of turns) total += count(t.content) + PER_TURN_OVERHEAD;
  return total;
}

/** 조립된 요청의 총 토큰을 추정한다(chat: messages, text: prompt). */
export function estimateRequestTokens(
  req: CompletionRequest,
  count: TokenCounter = heuristicTokenCount,
): number {
  if (req.prompt != null) return count(req.prompt);
  return estimateTurnsTokens(req.messages, count);
}

/** 컨텍스트 사용량 요약(UI 표시용). */
export interface ContextUsage {
  /** 추정 사용 토큰. */
  used: number;
  /** 목표 컨텍스트 크기(preset.sampling.contextSize). */
  budget: number;
  /** 0..1 사용 비율(budget=0이면 0). */
  ratio: number;
  /** 예산 초과 여부(트림이 일어났을 수 있음). */
  over: boolean;
  /** Reserved/generated output budget used for rough cost projection. */
  reservedOutputTokens?: number;
  /** Estimated input-side cost in USD. */
  inputCostUsd?: number;
  /** Estimated output-side cost in USD. */
  outputCostUsd?: number;
  /** Total estimated turn cost in USD. */
  totalCostUsd?: number;
  /** Price source used for the estimate. */
  pricingSource?: string;
}

/** 요청과 컨텍스트 한도로 사용량 요약을 만든다(결정 #12). */
export function contextUsage(
  req: CompletionRequest,
  count: TokenCounter = heuristicTokenCount,
): ContextUsage {
  const used = estimateRequestTokens(req, count);
  const budget = req.params.contextSize ?? 0;
  const ratio = budget > 0 ? used / budget : 0;
  const reservedOutputTokens = req.params.maxTokens ?? 0;
  const cost = estimateTokenCost(
    { promptTokens: used, completionTokens: reservedOutputTokens },
    resolvePricing(req.model, req.params),
  );
  return {
    used,
    budget,
    ratio,
    over: budget > 0 && used > budget,
    ...(reservedOutputTokens ? { reservedOutputTokens } : {}),
    ...(cost
      ? {
          inputCostUsd: cost.inputCostUsd,
          outputCostUsd: cost.outputCostUsd,
          totalCostUsd: cost.totalCostUsd,
          pricingSource: cost.pricingSource,
        }
      : {}),
  };
}
